/**
 * AvatarViewGizmos — the two viewport view modes the outliner offers on the rig
 * avatar (avatarViewStore): the skeleton drawn over the meshes, and the meshes
 * rendered lit / unlit / wireframe.
 *
 * Both read the store through useSyncExternalStore and find the avatar through
 * `movement.getGroup()` every frame — the same live handle the camera cage and
 * the see-through fade use — so they follow a character switch without being
 * told, and they wait for an avatar that has not finished loading rather than
 * asking React to re-render them when it does.
 *
 * Mounted in BOTH edit and live mode (Scene.jsx), like the torch and the
 * backdrop: a view mode is for the viewport you are looking at, and the editor
 * viewport is where these questions get asked.
 */
import { useEffect, useRef, useSyncExternalStore } from 'react'
import * as THREE from 'three'
import { useFrame, useThree } from '@react-three/fiber'
import { avatarViewStore } from '../lib/avatarViewStore'
import { applyMeshView } from '../lib/avatarViewApply'
import { movement } from '../lib/movementComponent'

const selectShowJoints = () => avatarViewStore.getState().showJoints
const selectMeshView = () => avatarViewStore.getState().meshView

/**
 * Skeleton overlay: one THREE.SkeletonHelper over the avatar root, rebuilt when
 * the root changes identity or when it gained bones after the helper was made
 * (the rig loads asynchronously; a helper built over an empty root has no
 * bones and would stay empty). Drawn on top (depthTest off, high renderOrder)
 * for the same reason the waypoint markers are: joints inside the mesh are the
 * point, and a depth-tested line inside a head is invisible.
 */
export function AvatarJointsHelper() {
  const show = useSyncExternalStore(avatarViewStore.subscribe, selectShowJoints, selectShowJoints)
  const { scene } = useThree()
  const helperRef = useRef(null)
  const rootRef = useRef(null)

  useEffect(() => {
    return () => {
      if (helperRef.current) {
        scene.remove(helperRef.current)
        helperRef.current.dispose()
        helperRef.current = null
      }
      if (typeof window !== 'undefined') window.__AVATAR_JOINTS_HELPER__ = null
    }
  }, [scene])

  useFrame(() => {
    const root = movement.getGroup()
    const helper = helperRef.current
    if (!show || !root) {
      if (helper) {
        scene.remove(helper)
        helper.dispose()
        helperRef.current = null
        rootRef.current = null
        if (typeof window !== 'undefined') window.__AVATAR_JOINTS_HELPER__ = null
      }
      return
    }
    // Rebuild when the root changed, or when the current helper saw no bones
    // (built before the rig arrived). SkeletonHelper collects bones once, at
    // construction, so it cannot pick them up later on its own.
    const stale = !helper || rootRef.current !== root || helper.bones.length === 0
    if (!stale) return
    if (helper) {
      scene.remove(helper)
      helper.dispose()
      helperRef.current = null
    }
    const next = new THREE.SkeletonHelper(root)
    if (next.bones.length === 0) {
      // Nothing to draw yet; try again next frame without keeping an empty helper.
      next.dispose()
      return
    }
    next.name = 'avatarJointsHelper'
    next.material.depthTest = false
    next.material.depthWrite = false
    next.material.transparent = true
    next.renderOrder = 9999
    scene.add(next)
    helperRef.current = next
    rootRef.current = root
    if (typeof window !== 'undefined') window.__AVATAR_JOINTS_HELPER__ = next
  })

  return null
}

/**
 * Mesh view mode applier: re-flags the avatar's materials when the mode, the
 * avatar root, or the material binding changes. The binding identity
 * (`window.__MATERIAL_BINDING__`, published by applyMaterialMapping) is what
 * changes when materials are rebuilt — a texture-tier upgrade, an inspector
 * PATCH, a Reload Materials — and a rebuilt material arrives lit, so the mode
 * has to be applied again. Three identity compares per frame, no allocation.
 */
export function AvatarMeshViewApplier() {
  const mode = useSyncExternalStore(avatarViewStore.subscribe, selectMeshView, selectMeshView)
  const lastRef = useRef({ root: null, mode: null, binding: undefined })

  useFrame(() => {
    const root = movement.getGroup()
    if (!root) return
    const binding = typeof window !== 'undefined' ? window.__MATERIAL_BINDING__ : undefined
    const last = lastRef.current
    if (last.root === root && last.mode === mode && last.binding === binding) return
    // A freshly loaded avatar is lit already; only spend the traverse when the
    // requested mode is not lit, or when we are leaving a non-lit mode.
    if (mode !== 'lit' || (last.mode !== null && last.mode !== 'lit' && last.root === root)) {
      const changed = applyMeshView(root, mode)
      // Debug readout for specs and the console: what was last applied, to how
      // many materials. Written only on an apply, so it costs nothing per frame.
      if (typeof window !== 'undefined') window.__AVATAR_MESH_VIEW_APPLIED__ = { mode, changed, at: performance.now() }
    }
    last.root = root
    last.mode = mode
    last.binding = binding
  })

  return null
}
